/**
 * Cliente HTTP da API.
 *
 * Concentra as chamadas à API em um módulo só, para que o restante do painel não conheça a URL
 * base nem o mecanismo de autenticação. O token é obtido do serviço de autenticação a cada chamada,
 * garantindo renovação transparente.
 */

import { validarDocumento, type DocumentoProposta } from '@emaster/dominio';

import { obterTokenValido } from '../autenticacao/servico.ts';

/**
 * URL base da API.
 *
 * `import.meta.env` é tipado como `any` pelo Vite, então o valor é estreitado explicitamente: sem
 * isso, um erro de digitação no nome da variável de ambiente viraria `undefined` concatenado na URL
 * e só apareceria como 404 em runtime.
 */
const API_BASE: string =
  typeof import.meta.env.VITE_API_URL === 'string' && import.meta.env.VITE_API_URL !== ''
    ? import.meta.env.VITE_API_URL
    : 'https://ymome7bh45.execute-api.us-east-2.amazonaws.com';

async function cabecalhos(): Promise<Record<string, string>> {
  const token = await obterTokenValido();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export interface RespostaApi<T = unknown> {
  readonly ok: boolean;
  readonly status: number;
  readonly dados: T;
}

async function chamar<T>(
  metodo: string,
  caminho: string,
  corpo?: unknown,
): Promise<RespostaApi<T>> {
  const r = await fetch(`${API_BASE}${caminho}`, {
    method: metodo,
    headers: await cabecalhos(),
    ...(corpo !== undefined && { body: JSON.stringify(corpo) }),
  });

  const dados = (await r
    .json()
    .catch(() => ({ erro: 'RESPOSTA_INVALIDA', mensagem: r.statusText }))) as T;

  return { ok: r.ok, status: r.status, dados };
}

export function publicarProposta(documento: unknown): Promise<RespostaApi> {
  return chamar('POST', '/propostas', documento);
}

export function atualizarProposta(numero: string, documento: unknown): Promise<RespostaApi> {
  return chamar('PUT', `/propostas/${encodeURIComponent(numero)}`, documento);
}

export interface PropostaObtida {
  readonly documento: DocumentoProposta;
  readonly versao: number;
  readonly url: string;
}

/**
 * Resultado da busca de uma proposta.
 *
 * União discriminada, e não `RespostaApi<PropostaObtida>`: em resposta de erro o corpo **não** é uma
 * proposta, e declarar que é obriga o chamador a converter à força justamente no caminho em que já
 * está lidando com problema. Aqui `if (!r.ok)` estreita o tipo, e o campo que não existe não é
 * alcançável.
 */
export type ResultadoProposta =
  | { readonly ok: true; readonly proposta: PropostaObtida }
  | { readonly ok: false; readonly mensagem: string };

/**
 * Busca uma proposta publicada e **valida** o documento recebido.
 *
 * Validar aqui não é zelo redundante. O painel e a Lambda compartilham o schema pelo código-fonte,
 * mas são publicados por caminhos independentes: o Amplify entrega o painel a cada push, e a Lambda
 * exige `update-function-code` à mão. Na janela entre os dois, o servidor devolve documento na forma
 * antiga, e sem validação o `as DocumentoProposta` é mentira — campo ausente chega `undefined`, o
 * tipo garante que não pode, e a tela de edição estourou com "Cannot read properties of undefined
 * (reading 'length')" ao ler `premissas.assessment`. Erro que não diz nada a quem vê e nada a quem
 * depura.
 *
 * O caso comum sai resolvido de graça: todo campo acrescentado ao schema entrou com `default`
 * justamente porque o acervo não o traz, então documento antigo é **normalizado** em vez de recusado.
 * Só forma de fato irrecuperável vira erro, e aí nomeando o campo.
 */
export async function obterProposta(numero: string): Promise<ResultadoProposta> {
  const r = await chamar<{ documento?: unknown; versao?: number; url?: string; mensagem?: string }>(
    'GET',
    `/propostas/${encodeURIComponent(numero)}`,
  );

  if (!r.ok) {
    return {
      ok: false,
      mensagem: r.dados.mensagem ?? `Erro ao carregar a proposta (HTTP ${r.status}).`,
    };
  }

  const validado = validarDocumento(r.dados.documento);

  if (!validado.valido || validado.dados === null) {
    /*
     * A lista é truncada porque schema divergente produz erro em cascata, e trinta linhas de
     * problema não ajudam ninguém a agir. Os três primeiros bastam para identificar a divergência.
     */
    const detalhe = validado.problemas
      .slice(0, 3)
      .map((p) => `${p.campo}: ${p.mensagem}`)
      .join('; ');

    return {
      ok: false,
      mensagem:
        `A proposta ${numero} está gravada num formato que este painel não reconhece (${detalhe}). ` +
        'Se o schema mudou recentemente, a Lambda da API provavelmente está numa versão anterior à do painel.',
    };
  }

  return {
    ok: true,
    proposta: { documento: validado.dados, versao: r.dados.versao ?? 1, url: r.dados.url ?? '' },
  };
}

export function listarPropostas(comoUsuario?: string): Promise<RespostaApi> {
  const q =
    comoUsuario !== undefined && comoUsuario !== ''
      ? `?comoUsuario=${encodeURIComponent(comoUsuario)}`
      : '';
  return chamar('GET', `/propostas${q}`);
}

export function listarUsuarios(): Promise<RespostaApi> {
  return chamar('GET', '/usuarios');
}

export function meuPerfil(): Promise<
  RespostaApi<{
    sub: string;
    email: string | null;
    perfil: string | null;
    nome: string | null;
    sobrenome: string | null;
    cargo: string | null;
    telefone: string | null;
    whatsapp: string | null;
    fotoUrl: string | null;
    cadastroCompleto: boolean;
    trilhas: readonly string[];
  }>
> {
  return chamar('GET', '/usuarios/eu');
}

export function alterarUsuario(
  sub: string,
  dados: {
    perfil?: string;
    ativo?: boolean;
    nome?: string;
    sobrenome?: string;
    cargo?: string;
    telefone?: string;
    trilhas?: readonly string[];
    fotoBase64?: string;
  },
): Promise<RespostaApi> {
  return chamar('PUT', `/usuarios/${encodeURIComponent(sub)}`, dados);
}

export function criarUsuario(dados: {
  email: string;
  nome: string;
  sobrenome: string;
  cargo: string;
  telefone: string;
  perfil: string;
  trilhas: readonly string[];
  senhaTemporaria: string;
}): Promise<RespostaApi> {
  return chamar('POST', '/usuarios', dados);
}

export function excluirUsuario(sub: string): Promise<RespostaApi> {
  return chamar('DELETE', `/usuarios/${encodeURIComponent(sub)}`);
}

export function obterCotacao(): Promise<
  RespostaApi<{ taxa: number; fonte: string; atualizadoEm: string; ehReserva: boolean }>
> {
  return chamar('GET', '/cambio');
}

export function atualizarMeuPerfil(dados: {
  nome?: string;
  sobrenome?: string;
  cargo?: string;
  telefone?: string;
  fotoBase64?: string;
  fotoUrl?: string | null;
}): Promise<
  RespostaApi<{
    sub: string;
    email: string | null;
    perfil: string | null;
    nome: string | null;
    sobrenome: string | null;
    cargo: string | null;
    telefone: string | null;
    whatsapp: string | null;
    fotoUrl: string | null;
    cadastroCompleto: boolean;
    trilhas: readonly string[];
  }>
> {
  return chamar('PUT', '/usuarios/eu', dados);
}
